// src/pages/admin/FeedbackAdmin.jsx
//
// Founder dashboard for beta feedback. Lists every doc in the
// `feedback` collection (newest first) with filters by type and
// status, and lets admin mark items as resolved or delete junk.
//
// Companion to:
//   - src/components/beta/BetaFeedbackButton.jsx  (writes the doc)
//   - functions/modules/feedback.js               (notifies founder by email)
//   - firestore.rules :: match /feedback/...      (gate admin reads/updates)

import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'
import {
  BugAntIcon,
  SparklesIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ClockIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

// Type metadata: icon, label, color tokens. Keep aligned with the
// composer chips in BetaFeedbackButton.jsx so the visual language
// stays consistent across submission and triage.
const TYPE_META = {
  bug: {
    label: 'Bug',
    Icon: BugAntIcon,
    border: 'border-red-200',
    bg: 'bg-red-50',
    text: 'text-red-700',
    pillBg: 'bg-red-100',
  },
  feature: {
    label: 'Feature',
    Icon: SparklesIcon,
    border: 'border-violet-200',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    pillBg: 'bg-violet-100',
  },
  question: {
    label: 'Question',
    Icon: ChatBubbleLeftEllipsisIcon,
    border: 'border-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    pillBg: 'bg-blue-100',
  },
  support: {
    label: 'Support',
    Icon: EnvelopeIcon,
    border: 'border-emerald-200',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    pillBg: 'bg-emerald-100',
  },
}

const TYPE_FILTERS = [
  { id: 'all',      label: 'All' },
  { id: 'support',  label: 'Support' },
  { id: 'bug',      label: 'Bugs' },
  { id: 'feature',  label: 'Features' },
  { id: 'question', label: 'Questions' },
]

const STATUS_FILTERS = [
  { id: 'open',     label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all',      label: 'All' },
]

function fmtRelative(ts) {
  if (!ts) return ''
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  const diff = Math.floor((Date.now() - d) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function buildReplyMailto(item) {
  if (!item.userEmail) return null
  const subject = encodeURIComponent('Re: your Scrimly feedback')
  const quoted = (item.message || '').split('\n').join('\n> ')
  const body = encodeURIComponent(
    `Hi,\n\nThanks for the feedback. Quick reply on what you sent:\n\n> ${quoted}\n\n\nKurt\nFounder, Scrimly\n`
  )
  return `mailto:${item.userEmail}?subject=${subject}&body=${body}`
}

function isMobileViewport(viewportStr) {
  if (!viewportStr) return false
  const w = Number(String(viewportStr).split('x')[0]) || 0
  return w > 0 && w < 768
}

function FeedbackCard({ item, onResolve, onReopen, onDelete }) {
  const meta = TYPE_META[item.type] || TYPE_META.question
  const { Icon } = meta
  const isResolved = item.status === 'resolved'
  const replyHref = buildReplyMailto(item)
  const onMobile = isMobileViewport(item.viewport)

  return (
    <div
      className={[
        'rounded-2xl border bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden',
        isResolved ? 'border-zinc-200 opacity-70' : meta.border,
      ].join(' ')}
    >
      {/* Header */}
      <div className={['flex items-start gap-3 px-5 py-3 border-b border-zinc-100', isResolved ? 'bg-zinc-50' : meta.bg].join(' ')}>
        <div className={['flex-shrink-0 h-9 w-9 rounded-xl flex items-center justify-center', meta.pillBg].join(' ')}>
          <Icon className={['h-5 w-5', meta.text].join(' ')} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={['text-[11px] font-bold uppercase tracking-wide', meta.text].join(' ')} style={{ fontFamily: HEAD_FONT }}>
              {meta.label}
            </span>
            {isResolved && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase">
                <CheckIcon className="h-3 w-3" /> Resolved
              </span>
            )}
            <span className="text-[11px] text-zinc-400 ml-auto inline-flex items-center gap-1">
              <ClockIcon className="h-3 w-3" />
              {fmtRelative(item.createdAt)}
            </span>
          </div>
          <p className="text-sm font-bold text-[#0d1f14] truncate mt-0.5">
            {item.displayName || item.userEmail || '(unknown user)'}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-3">
        <p className="text-sm text-[#0d1f14] whitespace-pre-line leading-relaxed">
          {item.message || '(empty)'}
        </p>

        {/* Context row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-zinc-500">
          {item.userEmail && (
            <span className="inline-flex items-center gap-1">
              <EnvelopeIcon className="h-3.5 w-3.5" />
              {item.userEmail}
            </span>
          )}
          {item.role && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              role: <strong className="font-semibold text-zinc-600">{item.role}</strong>
            </span>
          )}
          {item.pagePath && (
            <a
              href={item.pageUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#5a8a1f] hover:text-[#3d6010] hover:underline"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              {item.pagePath}
            </a>
          )}
          {item.viewport && (
            <span className="inline-flex items-center gap-1">
              {onMobile ? <DevicePhoneMobileIcon className="h-3.5 w-3.5" /> : <ComputerDesktopIcon className="h-3.5 w-3.5" />}
              {item.viewport}
            </span>
          )}
        </div>

        {/* Browser line, collapsed because it's noisy */}
        {item.userAgent && (
          <details className="text-[10px] text-zinc-400">
            <summary className="cursor-pointer hover:text-zinc-600">Browser</summary>
            <p className="mt-1 break-all font-mono">{item.userAgent}</p>
          </details>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-zinc-100 bg-zinc-50 px-5 py-3 flex flex-wrap gap-2 justify-end">
        {replyHref && (
          <a
            href={replyHref}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <EnvelopeIcon className="h-3.5 w-3.5" />
            Reply
          </a>
        )}
        {!isResolved ? (
          <button
            type="button"
            onClick={() => onResolve(item.id)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 text-xs font-bold uppercase transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <CheckIcon className="h-3.5 w-3.5" />
            Mark Resolved
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onReopen(item.id)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-zinc-700 hover:bg-zinc-100 transition-all"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            Reopen
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="inline-flex items-center gap-1.5 rounded-lg text-xs font-bold uppercase text-red-500 hover:bg-red-50 px-3 py-1.5 transition-all"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          <TrashIcon className="h-3.5 w-3.5" />
          Delete
        </button>
      </div>
    </div>
  )
}

export default function FeedbackAdmin() {
  const { firebaseUser } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('open')

  // Live subscription so a new submission shows up without a refresh.
  // Capped implicitly by Firestore's default batch size for now since
  // total feedback volume is small; switch to a paged query later if
  // it ever crosses ~500 docs.
  useEffect(() => {
    const q = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoading(false)
      },
      (err) => {
        console.error('[FeedbackAdmin] snapshot failed', err)
        setError(err?.message || 'Could not load feedback.')
        setLoading(false)
      }
    )
    return unsub
  }, [])

  const visible = useMemo(() => {
    return items.filter((it) => {
      if (typeFilter !== 'all' && it.type !== typeFilter) return false
      if (statusFilter === 'open' && it.status === 'resolved') return false
      if (statusFilter === 'resolved' && it.status !== 'resolved') return false
      return true
    })
  }, [items, typeFilter, statusFilter])

  const counts = useMemo(() => {
    const open = items.filter((i) => i.status !== 'resolved').length
    const resolved = items.filter((i) => i.status === 'resolved').length
    const bugs = items.filter((i) => i.type === 'bug' && i.status !== 'resolved').length
    return { total: items.length, open, resolved, bugs }
  }, [items])

  const handleResolve = async (id) => {
    try {
      await updateDoc(doc(db, 'feedback', id), {
        status: 'resolved',
        resolvedAt: serverTimestamp(),
        resolvedBy: firebaseUser?.uid || null,
      })
      toast.success('Marked resolved')
    } catch (err) {
      console.error('[FeedbackAdmin] resolve failed', err)
      toast.error('Could not update')
    }
  }

  const handleReopen = async (id) => {
    try {
      await updateDoc(doc(db, 'feedback', id), {
        status: 'open',
        resolvedAt: null,
        resolvedBy: null,
      })
      toast.success('Reopened')
    } catch (err) {
      console.error('[FeedbackAdmin] reopen failed', err)
      toast.error('Could not update')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return
    try {
      await deleteDoc(doc(db, 'feedback', id))
      toast.success('Deleted')
    } catch (err) {
      console.error('[FeedbackAdmin] delete failed', err)
      toast.error('Could not delete')
    }
  }

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Beta
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14] mt-0.5"
              style={{ fontFamily: HEAD_FONT }}
            >
              Feedback Inbox
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Bugs, feature ideas, and questions submitted via the floating bug button.
            </p>
          </div>
          <Link
            to="/admin/dashboard"
            className="text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010]"
          >
            ← Admin dashboard
          </Link>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', value: counts.total, color: 'text-[#0d1f14]' },
            { label: 'Open', value: counts.open, color: 'text-amber-600' },
            { label: 'Open Bugs', value: counts.bugs, color: 'text-red-600' },
            { label: 'Resolved', value: counts.resolved, color: 'text-emerald-600' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-3 text-center">
              <p className={['text-2xl font-bold', s.color].join(' ')} style={{ fontFamily: HEAD_FONT }}>{s.value}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex rounded-xl border border-zinc-200 bg-white p-1 gap-1 self-start">
            {TYPE_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTypeFilter(tab.id)}
                className={[
                  'px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap',
                  typeFilter === tab.id
                    ? 'bg-[#b5f03d] text-[#0d1f14] shadow-sm'
                    : 'text-zinc-500 hover:text-[#0d1f14]',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-xl border border-zinc-200 bg-white p-1 gap-1 self-start">
            {STATUS_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={[
                  'px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap',
                  statusFilter === tab.id
                    ? 'bg-[#0d1f14] text-white shadow-sm'
                    : 'text-zinc-500 hover:text-[#0d1f14]',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400">
            Loading feedback...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-20">
            <FunnelIcon className="mx-auto h-10 w-10 text-zinc-300 mb-4" />
            <p className="text-sm text-zinc-500">
              {items.length === 0
                ? 'No feedback yet. Once beta testers submit, it lands here.'
                : 'Nothing matches your filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((item) => (
              <FeedbackCard
                key={item.id}
                item={item}
                onResolve={handleResolve}
                onReopen={handleReopen}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
